// components/Dashboard/tabs/TagsTab.js
import React, { useState, useMemo } from 'react';
import './TagsTab.css';

const TagsTab = ({
  tags,
  contents = [],
  contentTagStats = [],
  onAddTag,
  onUpdateTag,
  onDeleteTag,
  showMessage,
  API_URL
}) => {
  const [newTag, setNewTag] = useState({ name: '', color: '#b87333' });
  const [editingTag, setEditingTag] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name, usage, recent
  const [showAddForm, setShowAddForm] = useState(false);

  // Preset renkler
  const presetColors = [
    '#b87333', '#e74c3c', '#e67e22', '#f1c40f', '#2ecc71',
    '#1abc9c', '#3498db', '#9b59b6', '#e84393', '#6c5ce7',
    '#00cec9', '#fd79a8', '#636e72', '#2d3436', '#a29bfe'
  ];

  const handleAddTag = async () => {
    const normalizedName = newTag.name.trim().toLowerCase();

    if (!normalizedName) {
      showMessage('❌ Etiket adı boş olamaz', 'error');
      return;
    }

    if (tags.some(tag => tag.name === normalizedName)) {
      showMessage('❌ Bu etiket zaten mevcut', 'error');
      return;
    }

    const success = await onAddTag({
      ...newTag,
      name: normalizedName
    });

    if (success) {
      setNewTag({ name: '', color: '#b87333' });
      setShowAddForm(false);
    }
  };

  const handleUpdateTag = async (tagId) => {
    if (!editingTag.name.trim()) {
      showMessage('❌ Etiket adı boş olamaz', 'error');
      return;
    }

    const normalizedName = editingTag.name.trim().toLowerCase();

    if (tags.some(tag => tag.id !== tagId && tag.name === normalizedName)) {
      showMessage('❌ Bu etiket adı zaten kullanılıyor', 'error');
      return;
    }

    const success = await onUpdateTag(tagId, { ...editingTag, name: normalizedName });
    if (success) {
      setEditingTag(null);
    }
  };

  const getTagUsageCount = (tagId) => {
    if (contentTagStats && contentTagStats.length > 0) {
      const stat = contentTagStats.find(s => s.id === tagId);
      return stat ? stat.usage_count : 0;
    }
    return contents.filter(content => {
      if (content.content_tags && Array.isArray(content.content_tags)) {
        return content.content_tags.some(tag => tag.id === tagId);
      }
      return false;
    }).length;
  };

  // Memoized hesaplamalar
  const tagStats = useMemo(() => {
    const stats = {};
    tags.forEach(tag => {
      stats[tag.id] = getTagUsageCount(tag.id);
    });
    return stats;
  }, [tags, contents, contentTagStats]);

  const totalUsage = useMemo(() => {
    return Object.values(tagStats).reduce((sum, count) => sum + count, 0);
  }, [tagStats]);

  const unusedCount = useMemo(() => {
    return Object.values(tagStats).filter(count => count === 0).length;
  }, [tagStats]);

  const mostPopularTag = useMemo(() => {
    if (tags.length === 0) return null;
    return tags.reduce((max, tag) =>
      (tagStats[tag.id] || 0) > (tagStats[max.id] || 0) ? tag : max
    );
  }, [tags, tagStats]);

  // Filtreleme ve sıralama
  const filteredTags = useMemo(() => {
    let result = tags.filter(tag =>
      tag.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    switch (sortBy) {
      case 'usage':
        result.sort((a, b) => (tagStats[b.id] || 0) - (tagStats[a.id] || 0));
        break;
      case 'recent':
        result.sort((a, b) => b.id - a.id);
        break;
      default: // name
        result.sort((a, b) => a.name.localeCompare(b.name, 'tr'));
        break;
    }

    return result;
  }, [tags, searchQuery, sortBy, tagStats]);

  return (
    <div className="tt-wrapper">

      {/* Üst Bar - İstatistikler */}
      <div className="tt-top-bar">
        <div className="tt-stat-pills">
          <div className="tt-pill">
            <span className="tt-pill-value">{tags.length}</span>
            <span className="tt-pill-label">Etiket</span>
          </div>
          <div className="tt-pill tt-pill-accent">
            <span className="tt-pill-value">{totalUsage}</span>
            <span className="tt-pill-label">Kullanım</span>
          </div>
          {mostPopularTag && tagStats[mostPopularTag.id] > 0 && (
            <div className="tt-pill tt-pill-highlight">
              <span className="tt-pill-badge" style={{ backgroundColor: mostPopularTag.color }}></span>
              <span className="tt-pill-value">{mostPopularTag.name}</span>
              <span className="tt-pill-label">En Popüler</span>
            </div>
          )}
          {unusedCount > 0 && (
            <div className="tt-pill tt-pill-warning">
              <span className="tt-pill-value">{unusedCount}</span>
              <span className="tt-pill-label">Kullanılmayan</span>
            </div>
          )}
        </div>

        <button
          className={`tt-add-trigger ${showAddForm ? 'active' : ''}`}
          onClick={() => setShowAddForm(!showAddForm)}
        >
          <span className="tt-add-trigger-icon">{showAddForm ? '✕' : '+'}</span>
          <span className="tt-add-trigger-text">{showAddForm ? 'Kapat' : 'Yeni Etiket'}</span>
        </button>
      </div>

      {/* Yeni Etiket Ekleme Formu */}
      <div className={`tt-add-panel ${showAddForm ? 'open' : ''}`}>
        <div className="tt-add-panel-inner">
          <div className="tt-add-row">
            <div className="tt-add-input-group">
              <input
                type="text"
                placeholder="Etiket adı girin..."
                value={newTag.name}
                onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
                onKeyPress={(e) => {
                  if (e.key === 'Enter') handleAddTag();
                }}
                className="tt-add-input"
                autoFocus={showAddForm}
              />
              {newTag.name.trim() && (
                <div className="tt-add-preview">
                  <span
                    className="tt-preview-chip"
                    style={{ backgroundColor: newTag.color }}
                  >
                    {newTag.name.trim().toLowerCase()}
                  </span>
                </div>
              )}
            </div>

            <button onClick={handleAddTag} className="tt-add-btn" disabled={!newTag.name.trim()}>
              Ekle
            </button>
          </div>

          <div className="tt-color-row">
            <span className="tt-color-row-label">Renk seçin:</span>
            <div className="tt-color-presets">
              {presetColors.map(color => (
                <button
                  key={color}
                  className={`tt-color-dot ${newTag.color === color ? 'active' : ''}`}
                  style={{ backgroundColor: color }}
                  onClick={() => setNewTag({ ...newTag, color })}
                  title={color}
                />
              ))}
              <label className="tt-color-custom">
                <input
                  type="color"
                  value={newTag.color}
                  onChange={(e) => setNewTag({ ...newTag, color: e.target.value })}
                />
                <span className="tt-color-custom-icon">🎨</span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Arama ve Sıralama */}
      <div className="tt-toolbar">
        <div className="tt-search-wrap">
          <span className="tt-search-icon">🔍</span>
          <input
            type="text"
            placeholder="Etiket ara..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="tt-toolbar-search"
          />
          {searchQuery && (
            <button className="tt-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>

        <div className="tt-sort-group">
          {[
            { key: 'name', label: 'A-Z' },
            { key: 'usage', label: 'Kullanım' },
            { key: 'recent', label: 'Yeni' }
          ].map(opt => (
            <button
              key={opt.key}
              className={`tt-sort-btn ${sortBy === opt.key ? 'active' : ''}`}
              onClick={() => setSortBy(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Etiket Listesi */}
      <div className="tt-list">
        {filteredTags.length === 0 ? (
          <div className="tt-empty">
            {searchQuery ? (
              <>
                <div className="tt-empty-icon">🔍</div>
                <p className="tt-empty-text">"{searchQuery}" ile eşleşen etiket bulunamadı</p>
                <button onClick={() => setSearchQuery('')} className="tt-empty-btn">
                  Aramayı Temizle
                </button>
              </>
            ) : (
              <>
                <div className="tt-empty-icon">🏷️</div>
                <p className="tt-empty-text">Henüz etiket eklenmemiş</p>
                <button onClick={() => setShowAddForm(true)} className="tt-empty-btn">
                  + İlk Etiketi Oluştur
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="tt-grid">
            {filteredTags.map(tag => {
              const usageCount = tagStats[tag.id] || 0;

              return (
                <div key={tag.id} className="tt-card">
                  {editingTag?.id === tag.id ? (
                    /* Düzenleme Modu */
                    <div className="tt-edit-mode">
                      <div className="tt-edit-top">
                        <input
                          type="text"
                          value={editingTag.name}
                          onChange={(e) => setEditingTag({ ...editingTag, name: e.target.value })}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleUpdateTag(tag.id);
                          }}
                          className="tt-edit-input"
                          autoFocus
                        />
                      </div>

                      <div className="tt-edit-colors">
                        {presetColors.slice(0, 10).map(color => (
                          <button
                            key={color}
                            className={`tt-color-dot small ${editingTag.color === color ? 'active' : ''}`}
                            style={{ backgroundColor: color }}
                            onClick={() => setEditingTag({ ...editingTag, color })}
                          />
                        ))}
                        <label className="tt-color-custom small">
                          <input
                            type="color"
                            value={editingTag.color}
                            onChange={(e) => setEditingTag({ ...editingTag, color: e.target.value })}
                          />
                          <span className="tt-color-custom-icon">🎨</span>
                        </label>
                      </div>

                      <div className="tt-edit-preview">
                        <span className="tt-preview-chip" style={{ backgroundColor: editingTag.color }}>
                          {editingTag.name || 'Önizleme'}
                        </span>
                      </div>

                      <div className="tt-edit-actions">
                        <button
                          onClick={() => handleUpdateTag(tag.id)}
                          className="tt-edit-save"
                        >
                          ✓ Kaydet
                        </button>
                        <button
                          onClick={() => setEditingTag(null)}
                          className="tt-edit-cancel"
                        >
                          İptal
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Normal Görünüm */
                    <>
                      <div className="tt-card-top">
                        <span
                          className="tt-tag-chip"
                          style={{ backgroundColor: tag.color }}
                        >
                          {tag.name}
                        </span>
                        <span className="tt-usage-pill" title={`${usageCount} içerikte kullanılıyor`}>
                          {usageCount}
                        </span>
                      </div>

                      <div className="tt-card-bottom">
                        {usageCount > 0 && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              const btn = e.currentTarget;
                              const originalText = btn.textContent;
                              btn.textContent = '⏳';
                              btn.disabled = true;
                              showMessage(`⏳ ${tag.name} medyaları hazırlanıyor...`, 'info');

                              try {
                                const token = localStorage.getItem('token');
                                const response = await fetch(`${API_URL}/tags/${tag.id}/download-media`, {
                                  headers: { 'Authorization': `Bearer ${token}` }
                                });
                                if (!response.ok) {
                                  let errMsg = 'İndirme hatası';
                                  try {
                                    const err = await response.json();
                                    errMsg = err.error || errMsg;
                                  } catch (e) { }
                                  showMessage('❌ ' + errMsg, 'error');
                                  return;
                                }
                                const blob = await response.blob();
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = `${tag.name}_medya.zip`;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                window.URL.revokeObjectURL(url);
                                showMessage(`${tag.name} medyaları indirildi!`, 'success');
                              } catch (err) {
                                console.error('İndirme hatası:', err);
                                showMessage('❌ İndirme sırasında hata oluştu', 'error');
                              } finally {
                                btn.textContent = '📥';
                                btn.disabled = false;
                              }
                            }}
                            className="tt-action-btn tt-action-download"
                            title="Medyaları indir (ZIP)"
                          >
                            📥
                          </button>
                        )}
                        <button
                          onClick={() => setEditingTag(tag)}
                          className="tt-action-btn tt-action-edit"
                          title="Düzenle"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => {
                            if (usageCount > 0) {
                              if (window.confirm(`Bu etiket ${usageCount} içerikte kullanılıyor. Yine de silmek istiyor musunuz?`)) {
                                onDeleteTag(tag.id);
                              }
                            } else {
                              onDeleteTag(tag.id);
                            }
                          }}
                          className="tt-action-btn tt-action-delete"
                          title="Sil"
                        >
                          🗑️
                        </button>
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TagsTab;